// src/api/translations.js
import dynamicAPI from './dynamicAPI';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMessage } from '../context/AdminMessageContext';
import { useTranslation } from 'react-i18next';

// Standalone: fetch the authenticated admin bundle (admin + shared scopes) for a
// language. Used to merge admin-only strings into i18next after login — these
// strings are deliberately NOT in the public /locales file.
export async function fetchAdminBundle(language) {
  const response = await dynamicAPI(
    'get',
    `/api/auth/translations/admin-bundle?lang=${encodeURIComponent(language)}`
  );
  // { lang, data } (plus dynamicAPI's status/isSuccess wrapper)
  return response;
}

// Standalone: list every bucket (all languages/groups/scopes). Used by the
// editor to discover languages, groups and scopes.
export async function fetchAllBuckets() {
  const response = await dynamicAPI('post', '/api/auth/translations', {});
  return response.translations || [];
}

// Hook: read/write a single (language, group, scope) bucket.
export default function useTranslationsAPI(language, group, scope) {
  const { setSuccessMessage, setErrorMessage } = useMessage();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const fetchTranslations = async () => {
    const response = await dynamicAPI('post', `/api/auth/translations`, {
      lang: language,
      group,
      scope,
    });
    return response.data || response;
  };

  // `data` is the flat { key: value } map for this bucket.
  const updateTranslations = async (data) => {
    const response = await dynamicAPI('put', `/api/auth/translations`, {
      lang: language,
      group,
      scope,
      data,
    });
    return response;
  };

  const generateTranslations = async () => {
    const response = await dynamicAPI('post', '/api/auth/translations', { generate_files: true });
    return response;
  };

  const translationsQuery = useQuery(
    ['translations', language, group, scope],
    fetchTranslations,
    {
      onError: (error) => {
        setErrorMessage(t('Error fetching translations') + error);
      },
    }
  );

  const updateMutation = useMutation(updateTranslations, {
    onSuccess: (data) => {
      setSuccessMessage(data.message || t('Translations saved successfully'));
      queryClient.invalidateQueries(['translations', language, group, scope]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.error || t('Error saving translations');
      setErrorMessage(errorMessage);
    },
  });

  const generateMutation = useMutation(generateTranslations, {
    onSuccess: (data) => {
      setSuccessMessage(data.message || t('Translations generated successfully'));
      queryClient.invalidateQueries(['translations']);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.error || t('Error generating translations');
      setErrorMessage(errorMessage);
    },
  });

  return { translationsQuery, updateMutation, generateMutation };
}
